/**
 * `apps/web/src/plugins/` — kernel plugins specific to THIS app.
 *
 * Native OAuth deep links, as a plugin.
 *
 * Migrated from `providers/deep-linking-provider.tsx`.
 * Lifecycle is managed by the plugin kernel with capabilities: ["deepLinks"].
 */
import type { useNavigate } from "@tanstack/solid-router";
import { safeRedirectTarget } from "@apunte-digital/shared";
import type { Plugin } from "@fleet/kernel";
import { finishSocialLogin, socialErrorMessage } from "@/lib/social-login";
import {
  clearTokenPresence,
  LEGACY_TOKEN_ACCOUNT,
  markTokenPresent,
  promoteOauthToken,
} from "@/lib/secure-token";
import { showToast } from "@/components/ui/toast";

interface OauthCallbackEvent {
  status: string;
  redirect?: string;
  error?: string;
}

export interface DeepLinkingConfig {
  /** Supplied by the host so this module never reaches into the i18n context. */
  translate: Parameters<typeof socialErrorMessage>[0];
}

export const deepLinkingPlugin: Plugin<DeepLinkingConfig> = {
  id: "deep-linking",
  // Web builds never mount it, so they never even import `@tauri-apps/api`.
  capabilities: ["deepLinks"],

  data(ctx) {
    const navigate: ReturnType<typeof useNavigate> = async (opts) => {
      const { router } = await import("@/router");
      await router.navigate(opts);
    };
    let unlisten: (() => void) | undefined;
    let disposed = false;

    async function handleOauthCallback(payload: OauthCallbackEvent): Promise<void> {
      if (payload.status !== "ok") {
        showToast(socialErrorMessage(ctx.config.translate, payload.error ?? "unknown"), "error");
        void navigate({ to: "/login", replace: true });
        return;
      }

      const redirect = safeRedirectTarget(payload.redirect ?? "/");
      markTokenPresent(LEGACY_TOKEN_ACCOUNT);

      await finishSocialLogin({
        navigate,
        redirect,
        onUserResolved: async (userId) => {
          markTokenPresent(userId);
          await promoteOauthToken(userId);
          clearTokenPresence(LEGACY_TOKEN_ACCOUNT);
        },
      });
    }

    void (async () => {
      const { listen } = await import("@tauri-apps/api/event");
      const un = await listen<OauthCallbackEvent>("oauth-callback", (event) => {
        void handleOauthCallback(event.payload);
      });
      if (disposed) {
        un();
      } else {
        unlisten = un;
      }
    })();

    ctx.addCleanup(() => {
      disposed = true;
      unlisten?.();
    });
    ctx.log("listening", { event: "oauth-callback" });
  },
};
